// Abre la vista de presentador en una ventana aparte y, si el navegador lo permite, la
// manda a la otra pantalla. Ver docs/vista-presentador.md.

const WINDOW_NAME = "sota-presenter";

// Window Management API (Chrome/Edge). No está en lib.dom, así que se tipa lo mínimo.
export interface ScreenInfo {
  label: string;
  left: number;
  top: number;
  width: number;
  height: number;
  availLeft: number;
  availTop: number;
  availWidth: number;
  availHeight: number;
  isPrimary: boolean;
}

export interface ScreenDetails extends EventTarget {
  screens: ScreenInfo[];
  currentScreen: ScreenInfo;
}

type WindowWithScreens = Window & { getScreenDetails?: () => Promise<ScreenDetails> };

export function canManageScreens(target: Window = window): boolean {
  return typeof (target as WindowWithScreens).getScreenDetails === "function";
}

/** Pantallas disponibles, o null si no hay soporte o el usuario negó el permiso. */
export async function getScreens(target: Window = window): Promise<ScreenDetails | null> {
  const getScreenDetails = (target as WindowWithScreens).getScreenDetails;
  if (!getScreenDetails) return null;
  try {
    return await getScreenDetails.call(target);
  } catch {
    return null;
  }
}

/** true si el permiso de pantallas ya está concedido (no abre el diálogo de permiso). */
export async function screensAlreadyGranted(): Promise<boolean> {
  try {
    const status = await navigator.permissions.query({
      name: "window-management" as PermissionName,
    });
    return status.state === "granted";
  } catch {
    return false;
  }
}

// Tamaño estándar de la vista: cómodo para leer sin ocupar toda la pantalla (en un monitor
// ultra ancho, llenarla dejaba líneas de texto larguísimas).
const VIEW_WIDTH = 1100;
const VIEW_HEIGHT = 760;

/** Rectángulo de tamaño estándar centrado en una pantalla (recortado si es más chica). */
function placement(screen: ScreenInfo): { left: number; top: number; width: number; height: number } {
  const width = Math.min(VIEW_WIDTH, screen.availWidth);
  const height = Math.min(VIEW_HEIGHT, screen.availHeight);
  return {
    left: Math.round(screen.availLeft + (screen.availWidth - width) / 2),
    top: Math.round(screen.availTop + (screen.availHeight - height) / 2),
    width,
    height,
  };
}

function popupFeatures(screen?: ScreenInfo): string {
  if (!screen) return `popup,width=${VIEW_WIDTH},height=${VIEW_HEIGHT}`;
  const { left, top, width, height } = placement(screen);
  return `popup,left=${left},top=${top},width=${width},height=${height}`;
}

/** Mueve una ventana a una pantalla, al tamaño estándar y centrada. Puede lanzar. */
function place(target: Window, screen: ScreenInfo): void {
  const { left, top, width, height } = placement(screen);
  // Primero el tamaño: si la ventana venía más grande, al moverla no queda fuera de borde.
  target.resizeTo(width, height);
  target.moveTo(left, top);
}

function isOnScreen(target: Window, screen: ScreenInfo): boolean {
  const x = target.screenLeft + target.outerWidth / 2;
  const y = target.screenTop + target.outerHeight / 2;
  return (
    x >= screen.left && x < screen.left + screen.width && y >= screen.top && y < screen.top + screen.height
  );
}

const wait = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

/**
 * Lleva ESTA ventana a otra pantalla. Debe llamarse desde un gesto del usuario. Prueba,
 * en orden, lo que el navegador permita:
 *  1. Moverla, al tamaño estándar y centrada (solo funciona en ventanas emergentes, no
 *     en pestañas).
 *  2. Ponerla en pantalla completa en esa pantalla.
 *  3. Reabrirla allá como ventana emergente y cerrar esta (hereda la sesión de la pestaña).
 * Devuelve cómo quedó, o "failed" si nada funcionó.
 */
export async function moveSelfToScreen(
  screen: ScreenInfo,
): Promise<"moved" | "fullscreen" | "reopened" | "failed"> {
  try {
    if (document.fullscreenElement) await document.exitFullscreen();
    place(window, screen);
  } catch {
    // sigue con la siguiente opción
  }
  await wait(250);
  if (isOnScreen(window, screen)) return "moved";

  try {
    // `screen` es una extensión de Window Management a requestFullscreen.
    await document.documentElement.requestFullscreen({ screen } as FullscreenOptions);
    return "fullscreen";
  } catch {
    // sigue con la siguiente opción
  }

  const reopened = window.open(window.location.href, "_blank", popupFeatures(screen));
  if (reopened) {
    window.close();
    return "reopened";
  }
  return "failed";
}

// Detalles de pantallas ya concedidos en esta pestaña. Con ellos, la ventana se abre
// directamente en la otra pantalla en vez de abrirse y moverse después.
let knownScreens: ScreenDetails | null = null;

/** Pide (o reutiliza) los detalles de pantallas y los recuerda para la próxima apertura. */
export async function rememberScreens(): Promise<ScreenDetails | null> {
  knownScreens ??= await getScreens();
  return knownScreens;
}

const otherScreen = (details: ScreenDetails) =>
  details.screens.find((screen) => screen !== details.currentScreen);

/**
 * Abre (o reutiliza) la ventana de presentador para un deck. Debe llamarse dentro de un
 * gesto del usuario (tecla o click) para que el navegador no bloquee el popup.
 * Con más de una pantalla y permiso, va a la pantalla donde NO está el deck; sin soporte
 * o sin permiso se queda donde abrió y se elige la pantalla desde la propia vista.
 */
export function openPresenterWindow(locale: string, deckId: string): Window | null {
  const url = `/presentador/${encodeURIComponent(locale)}/${encodeURIComponent(deckId)}`;
  const target = knownScreens && knownScreens.screens.length > 1 ? otherScreen(knownScreens) : undefined;

  const popup = window.open(url, WINDOW_NAME, popupFeatures(target));
  if (!popup) return null;
  popup.focus();

  if (!target) {
    // Primera vez: se pide el permiso y, si hay otra pantalla, se mueve la ventana ya abierta.
    void rememberScreens().then((details) => {
      if (!details || details.screens.length < 2) return;
      const other = otherScreen(details);
      if (!other) return;
      try {
        place(popup, other);
      } catch {
        // el navegador puede rechazarlo; se elige la pantalla desde la vista
      }
    });
  }

  return popup;
}
